import type {
  HamDbResponseType,
  LookupServiceProviderType,
  LookupServiceResponseType
} from "@/types";
import { ProviderCallsignNotFoundError, ProviderUnavailableError } from "@/utils/errors/LookupServiceErrors";
import type { LookupServiceInterface } from "@/interfaces/LookupServiceInterface";
import LookupServiceResponseTransformer from "@/transformers/LookupServiceResponseTransformer";
// Use the canonical /v1/ path: hitting the bare path returns a 302 to
// http://api.hamdb.org/v1/... which iOS ATS blocks (cleartext redirect).
const HAM_DB_ENDPOINT = "https://api.hamdb.org/v1";

class HamDbService implements LookupServiceInterface {
  readonly providerName: LookupServiceProviderType = "HAMDB";
  readonly requiresAuthentication: boolean = false;
  readonly requiresConnectivity: boolean = true;
  readonly providesGridsquares: boolean = true;
  sessionKey?: string
  constructor(sessionKey: string = "") {
    this.sessionKey = sessionKey;
  }
  normalizeCallsignQuery(callsign: string): string {
    return callsign.split("/")[0].toUpperCase()
  }

  public async lookupCallsign(
    callsign: string,
    _sessionKey?: string // eslint-disable-line @typescript-eslint/no-unused-vars
  ): Promise<LookupServiceResponseType> {
    if (!callsign) {
      throw new Error("You must provide a callsign");
    }
    const normalizedCallsign = this.normalizeCallsignQuery(callsign);
    const response = await fetch(`${HAM_DB_ENDPOINT}/${normalizedCallsign}/json/HAMRS`);

    if (!response.ok) {
      // HAMDB returned an HTTP error -- not a client bug, so use typed errors the
      // UI surfaces without reporting to Sentry. A 5xx / timeout (e.g. 524) or a
      // 408/429 is a transient upstream problem; any other 4xx means HAMDB
      // rejected the callsign, which is effectively "not found".
      if (response.status >= 500 || response.status === 408 || response.status === 429) {
        throw new ProviderUnavailableError(
          this.providerName,
          `HAMDB is temporarily unavailable (${response.status}). Try again shortly.`
        );
      }
      throw new ProviderCallsignNotFoundError(this.providerName, "Callsign not found");
    }

    let data: HamDbResponseType;
    try {
      data = await response.json();
    } catch {
      // HAMDB returned a 200 with a malformed/truncated body (upstream/transient,
      // not a client bug). Surface as unavailable instead of a raw SyntaxError.
      throw new ProviderUnavailableError(
        this.providerName,
        "HAMDB returned an invalid response. Try again shortly."
      );
    }

    // Treat an unexpected shape (no callsign payload) the same as NOT_FOUND so a
    // malformed-but-valid JSON can't crash the downstream transformer.
    if (data?.hamdb?.messages?.status === "NOT_FOUND" || !data?.hamdb?.callsign) {
      throw new ProviderCallsignNotFoundError(
        this.providerName,
        "Callsign not found"
      );
    }
    const normalizeResponse = this.normalizeResponse(data);
    return normalizeResponse;
  }
  private normalizeResponse(
    response: HamDbResponseType
  ): LookupServiceResponseType {
    return LookupServiceResponseTransformer(response, this.providerName);
  }
}

export default HamDbService;